import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LOGIN_NUMBERS, ADMIN_NUMBER, ADMIN_PASSWORD, isAdmin } from '../../config/learners';
import { loginAs, getCurrentUserId } from '../../utils/session';
import { hasResumableProgress } from '../../utils/exerciseProgress';
import { logEvent } from '../../utils/experimentLog';

// 番号選択式ログイン。ID・パスワードの入力をなくし、
// 「一覧を開く」→「番号を選ぶ」→「はじめるを押す」の3タップで完了させる（授業時間の節約）。
// 名簿は config/learners.js の定数なのでサーバー往復が無く、
// 40名が同時にログインしても一覧は即時に表示される。
// ログイン後は番号によらず必ず Home へ行く（学習者が迷わないよう入口を1つにする）。
// 管理者番号（999）を選んだときだけパスワード欄を出し、合っていなければログインさせない（2026-10-19）。
function Ex1_2026_LearnerLogin() {
  const navigate = useNavigate();

  const currentUserId = getCurrentUserId();
  const [selected, setSelected] = useState(currentUserId);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);

  const canResume = selected != null && hasResumableProgress(selected);
  const needsPassword = isAdmin(selected);

  const handleChange = (e) => {
    const value = e.target.value;
    setSelected(value === '' ? null : parseInt(value, 10));
    setPassword('');
    setPasswordError(false);
  };

  const handleLogin = () => {
    if (selected == null) return;
    if (needsPassword && password !== ADMIN_PASSWORD) {
      setPasswordError(true);
      return;
    }
    loginAs(selected);
    // ログイン記録は loginAs の後に送る（ログに番号を載せるため）
    logEvent('login', { resumed: canResume, admin: isAdmin(selected) });
    navigate('/', { replace: true });
  };

  return (
    <main className="page login-page">
      <section className="login-sheet">
        <header className="login-sheet__head">
          <p className="login-sheet__eyebrow">ログイン</p>
          <h1 className="login-sheet__title">自分の番号を選んでください</h1>
          <p className="login-sheet__note">
            一覧から番号を選び、【はじめる】を押します。
            {currentUserId != null && `（いまは ${currentUserId}番 でログイン中です）`}
          </p>
        </header>

        <div className="login-body">
          <label className="login-select__label" htmlFor="login-number-select">
            番号
          </label>
          <select
            id="login-number-select"
            className="login-select"
            value={selected == null ? '' : String(selected)}
            onChange={handleChange}
          >
            <option value="">選択</option>
            {LOGIN_NUMBERS.map((n) => (
              <option key={n} value={n}>
                {n === ADMIN_NUMBER ? `${n}（管理者）` : `${n}番`}
              </option>
            ))}
          </select>
          {needsPassword && (
            <>
              <label className="login-select__label" htmlFor="login-admin-password">
                管理者パスワード
              </label>
              <input
                id="login-admin-password"
                type="password"
                className="login-select"
                value={password}
                autoComplete="off"
                onChange={(e) => { setPassword(e.target.value); setPasswordError(false); }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(); }}
              />
              {passwordError && <p className="login-error" role="alert">パスワードが違います</p>}
            </>
          )}
        </div>

        <footer className="login-footer">
          <p className="login-footer__status">
            {selected == null
              ? '番号を選ぶと【はじめる】が押せます'
              : canResume
                ? `${selected}番 の前回の続きから再開します`
                : `${selected}番 でよろしいですか？`}
          </p>
          <button
            type="button"
            className="btn-sheet btn-sheet--block"
            disabled={selected == null}
            onClick={handleLogin}
          >
            {selected == null ? '番号を選択' : `${selected}番 ではじめる`}
          </button>
        </footer>
      </section>
    </main>
  );
}

export default Ex1_2026_LearnerLogin;
